import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import IntlMessages from 'util/IntlMessages';
import PropTypes from 'prop-types';
import {
  Table,
  Card,
  Form,
  Input,
  Row,
  Col,
  Button,
} from 'antd';
import lodash from 'lodash';
import OrganizationCell from '@components/tables/cells/OrganizationCell';
import CustomerCell from '@components/tables/cells/CustomerCell';
import ActionsCell from '@components/tables/cells/ActionsCell';
import ListingsTableInputFilter from '@filters/ListingsTableInputFilter';
import { setSubtitle } from '@uhe_actions/SubtitleActions';
import {
  getNotifactions,
  getGlobalNotifications,
  updateGlobalNotifications,
  notifactionsOnFetchData,
} from '@actions/uhe/system/NotificationsActions';
import {
  shouldShowNotficationsActions,
  shouldSaveGlobalNotifications,
  shouldEditGlobalNotificationsInpunts,
  shouldShowSystemWideNotificationEmailSection,
  shouldValidateGlobalNotifications,
} from '@util/UheRoleChecker';
import {
  getLoggedUser,
  setObjectEmptyStringPropertiesToNull,
} from '@util/UheHelper';
import {
  APP_PAGES_CONTEXT,
  TABLE_FILTER_PREFIX,
  LISTING_TABLES_PAGE_SIZE,
  IS_EDIT_PAGE_REGEX,
} from '@constants/UHESettings';
import { shouldResetPaginationSuccess } from '@actions';

const layout = {
  labelCol: { span: 9 },
  wrapperCol: { span: 15 },
};

/**
* Use different cell type depending on the column
* @param  {Object} content Data for Each Cell
* @param  {string} key Column Name
* @return {ReactElement|Object} Table Cell || Object
*/
const cellRenderer = (content, key) => {
  let cell;

  switch (key) {
    case 'organization':
      cell = <OrganizationCell content={content} />;
      break;
    case 'customer':
      cell = <CustomerCell content={content} />;
      break;
    case 'actions':
      cell = (
        <ActionsCell
          content={content}
          loggedUser={getLoggedUser()}
          editLink={`/system/notifications/edit/${content.id}`}
          page={APP_PAGES_CONTEXT.notifications}
        />
      );
      break;
    default:
      cell = content;
  }

  return cell;
};

/**
 * Creates Object for the Table Columns
 * @param {string} columnName Column Name
 * @returns {object} Object Item for Table dataSource
 */
const columnsMapper = (columnName) => ({
  title: (cellData) => (
    <ListingsTableInputFilter
      cellData={cellData}
      title={`uhe.table.${columnName}`}
      dataKey={columnName}
      showFilter={false}
    />
  ),
  align: 'left',
  minWidth: 200,
  dataIndex: columnName,
  render: (content) => cellRenderer(content, columnName),
});

/**
 * Notifications Class Component
 */
class Notifications extends Component {
  /**
   * Adapt data to the Talbe
   * @param  {Array} data Notifications Table Data
   * @returns {Array} Notifications Table Cell Data
   */
  static dataAdapter(data = []) {
    const adaptedData = [];

    if (data) {
      data.forEach((value, index) => {
        adaptedData.push({
          key: index,
          organization: {
            name: value.customer.organization.name,
            id: value.customer.organization.id,
          },
          customer: {
            name: value.customer.name,
            id: value.customer.id,
          },
          notificationEmailOne: value.email1,
          notificationEmailTwo: value.email2,
          notificationEmailThree: value.email3,
          actions: {
            id: value.customer.id,
          },
        });
      });
    }

    return adaptedData;
  }

  /**
   * Notifications Constructor
   * @param {object} props Props
   */
  constructor(props) {
    super(props);
    const {
      subtitle, setSubtitle, history, intl,
    } = this.props;
    this.onPageChange = this.onPageChange.bind(this);
    this.formRef = React.createRef();
    this.columns = [];

    this.tableFilterMap = {
      organization: 'organization.name',
      customer: 'name',
      notificationEmailOne: 'email1',
      notificationEmailTwo: 'email2',
      notificationEmailThree: 'email3',
    };

    this.tableKeys = [
      'organization',
      'customer',
      'notificationEmailOne',
      'notificationEmailTwo',
      'notificationEmailThree',
      ...(shouldShowNotficationsActions() ? ['actions'] : []),
    ];

    this.filterTypes = {
      advancedReports: {
        type: 'dropdown',
        options: [
          { value: '1', label: intl.formatMessage({ id: 'common.on' }) },
          { value: '0', label: intl.formatMessage({ id: 'common.off' }) },
        ],
      },
    };

    this.tableKeys.forEach((value, index) => {
      const filter = this.filterTypes[value] || {};
      this.columns.push({
        title: (cellData) => (
          <ListingsTableInputFilter
            filterType={filter.type}
            filterOptions={filter.options}
            showFilter={value === 'organization' || value === 'customer'}
            cellData={cellData}
            title={`uhe.table.${value}`}
            dataKey={value}
            triggerCharsNum={value === 'organization' || value === 'customer' ? 0 : undefined}
          />
        ),
        align: index > 3 ? 'center' : 'left',
        minWidth: 200,
        dataIndex: value,
        render: (content) => cellRenderer(content, value),
      });
    });

    this.state = {
      globalNotifications: {
        email1: null,
        email2: null,
        email3: null,
      },
      tableColumns: this.columns,
    };

    if (subtitle && subtitle.langId !== 'notifications.title') {
      setSubtitle('notifications.title');
    }
    this.history = history;
    this.qParams = new URLSearchParams(this.history.location.search);
  }

  /**
   * componentDidMount() is invoked immediately after
   * a component is mounted (inserted into the tree)
   * @returns {void} void
   */
  componentDidMount() {
    const {
      notifactionsData,
      globalNotifications,
      globalNotificationsData,
      loggedUser,
      pagination,
      lastLocation,
      pathname,
      shouldResetPaginationFlag,
      resetPaginationSuccess,
    } = this.props;
    const { email1, email2, email3 } = globalNotifications;

    if (!IS_EDIT_PAGE_REGEX.test(lastLocation?.pathname) || shouldResetPaginationFlag) {
      this.onPageChange(1);
      resetPaginationSuccess();
      return;
    }
    this.onPageChange(pagination?.current || 1);

    notifactionsData();
    globalNotificationsData();

    if (Object.keys(globalNotifications).length > 0) {
      this.setState({
        globalNotifications,
      });

      if (shouldValidateGlobalNotifications(loggedUser)) {
        this.formRef.current.setFieldsValue({
          email1,
          email2,
          email3,
        });
      }
    }
  }

  /**
   * Set Props to Local State
   * @param {Object} prevProps previous props
   * @returns {void}
   */
  componentDidUpdate(prevProps) {
    const { globalNotifications, loggedUser } = this.props;
    if (
      Object.keys(prevProps.globalNotifications).length === 0
      && Object.keys(globalNotifications).length > 0
    ) {
      this.setState({ globalNotifications }, () => {
        const { globalNotifications } = this.state;
        const { email1, email2, email3 } = globalNotifications;

        if (shouldValidateGlobalNotifications(loggedUser)) {
          this.formRef.current.setFieldsValue({
            email1,
            email2,
            email3,
          });
        }
      });
    }

    if (prevProps.loggedUser !== loggedUser) {
      if (shouldShowNotficationsActions()) {
        if (!this.tableKeys.some((column) => column === 'actions')) {
          this.tableKeys.push('actions');
          this.setState({ tableColumns: this.tableKeys.map(columnsMapper) });
        }
      } else {
        this.tableKeys = this.tableKeys.filter((column) => column !== 'actions');
        this.setState({ tableColumns: this.tableKeys.map(columnsMapper) });
      }
    }

    const { location } = this.props;
    if (location.search !== prevProps.location.search) {
      this.qParams = new URLSearchParams(location.search);
      this.onPageChange(1);
    }
  }

  /**
   * Handle event in current page
   * @param  {number} page number
   * @return {void}
   */
  onPageChange(page) {
    const { notifactionsOnFetchData } = this.props;
    const filter = [];
    lodash.forOwn(this.tableFilterMap, (value, key) => {
      const filterParam = this.qParams.get(`${TABLE_FILTER_PREFIX}${key}`);

      if (filterParam) {
        if (this.filterTypes[key] && this.filterTypes[key].type === 'dropdown') {
          filter.push(`${value}=${encodeURIComponent(filterParam)}`);
        } else {
          filter.push(`${value}~=${encodeURIComponent(`%${filterParam}%`)}`);
        }
      }
    });

    this.filter = filter;

    notifactionsOnFetchData(page - 1, this.filter);
  }

  /**
   * Handle Changes in the Input Fields
   * @param {Object} event True or false
   * @param {string} key Input key's value
   * @returns {void}
   */
  changeHandler = (event, key) => {
    const { globalNotifications } = this.state;

    this.setState({
      globalNotifications: {
        ...globalNotifications,
        [key]: event.target.value,
      },
    });
  };

  /**
   * Invokes Save Notifications Action
   * @returns {void}
   */
  saveNotifications = () => {
    const { globalNotifications } = this.state;
    const { saveGlobalNotifications } = this.props;

    saveGlobalNotifications({ body: setObjectEmptyStringPropertiesToNull(globalNotifications) });
  };

  /**
  * Render table
  * @returns {JSX} render table
  */
  renderTable = () => {
    const {
      pagination, data, loading, loggedUser,
    } = this.props;
    const { tableColumns } = this.state;
    const { customer } = data;
    pagination.onChange = this.onPageChange;

    if (loggedUser.roles) {
      if (loggedUser.isCaregilitySystemAdmin) {
        if (this.columns.length <= 4) {
          const foundActions = this.tableKeys.filter((key) => key === 'actions');

          if (foundActions.length === 0) {
            this.tableKeys.push('actions');
          }

          this.tableKeys.filter((key) => key === 'actions').forEach((value, index) => {
            const filter = this.filterTypes[value] || {};
            this.columns.push({
              title: (cellData) => (
                <ListingsTableInputFilter
                  filterType={filter.type}
                  filterOptions={filter.options}
                  showFilter={value === 'organization' || value === 'customer'}
                  cellData={cellData}
                  title={`uhe.table.${value}`}
                  dataKey={value}
                  triggerCharsNum={value === 'organization' || value === 'customer' ? 0 : undefined}
                />
              ),
              align: index > 3 ? 'center' : 'left',
              minWidth: 200,
              dataIndex: value,
              render: (content) => cellRenderer(content, value),
            });
          });
        }
      }

      return (
        <Table
          className="notifications-table"
          dataSource={Notifications.dataAdapter(customer)}
          columns={tableColumns}
          pagination={pagination}
          loading={loading}
        />
      );
    }
  };

  /**
   * Renders Notifications Component
   * @returns {JSX.Element} Notifications Component
   */
  render() {
    const { globalNotifications } = this.state;
    const { email1, email2, email3 } = globalNotifications;

    return (
      <div>
        {shouldShowSystemWideNotificationEmailSection() && (
          <Form
            {...layout}
            ref={this.formRef}
            onFinish={this.saveNotifications}
            autoComplete={Math.random()}
          >
            <Card
              className="gx-card"
              title={<IntlMessages id="notifications.SWNE" />}
            >
              <Row>
                <Col lg={7} md={8} sm={24} sx={24}>
                  <Form.Item
                    className="form-item-row"
                    name="email1"
                    label={<IntlMessages id="notifications.notificationEmail1" />}
                    rules={[
                      {
                        type: 'email',
                        message: <IntlMessages id="common.error_email" />,
                      },
                    ]}
                    initialValue={email1}
                  >
                    <Input
                      autoComplete={Math.random()}
                      onChange={(event) => this.changeHandler(event, 'email1')}
                      disabled={shouldEditGlobalNotificationsInpunts()}
                    />
                  </Form.Item>
                </Col>
                <Col lg={7} md={8} sm={24} sx={24}>
                  <Form.Item
                    className="form-item-row"
                    name="email2"
                    label={<IntlMessages id="notifications.notificationEmail2" />}
                    rules={[
                      {
                        type: 'email',
                        message: <IntlMessages id="common.error_email" />,
                      },
                    ]}
                    initialValue={email2}
                  >
                    <Input
                      autoComplete={Math.random()}
                      onChange={(event) => this.changeHandler(event, 'email2')}
                      disabled={shouldEditGlobalNotificationsInpunts()}
                    />
                  </Form.Item>
                </Col>
                <Col lg={7} md={8} sm={24} sx={24}>
                  <Form.Item
                    className="form-item-row"
                    name="email3"
                    label={<IntlMessages id="notifications.notificationEmail3" />}
                    rules={[
                      {
                        type: 'email',
                        message: <IntlMessages id="common.error_email" />,
                      },
                    ]}
                    initialValue={email3}
                  >
                    <Input
                      autoComplete={Math.random()}
                      onChange={(event) => this.changeHandler(event, 'email3')}
                      disabled={shouldEditGlobalNotificationsInpunts()}
                    />
                  </Form.Item>
                </Col>
                <Col
                  lg={3}
                  md={8}
                  sm={24}
                  sx={24}
                  className="gx-d-flex gx-align-items-center"
                >
                  <Button
                    className="notifications-save-btn"
                    htmlType="submit"
                    disabled={shouldSaveGlobalNotifications()}
                  >
                    <IntlMessages id="button.save" />
                  </Button>
                </Col>
              </Row>
            </Card>
          </Form>
        )}
        <div className="uhe-table">
          {this.renderTable()}
        </div>
      </div>
    );
  }
}

Notifications.defaultProps = {
  loggedUser: {},
  loading: true,
  pagination: {
    pageSize: LISTING_TABLES_PAGE_SIZE,
    defaultCurrent: 1,
  },
  shouldResetPaginationFlag: false,
};

Notifications.propTypes = {
  data: PropTypes.shape().isRequired,
  globalNotifications: PropTypes.shape().isRequired,
  notifactionsData: PropTypes.func.isRequired,
  globalNotificationsData: PropTypes.func.isRequired,
  subtitle: PropTypes.shape().isRequired,
  setSubtitle: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  loggedUser: PropTypes.shape(),
  saveGlobalNotifications: PropTypes.func.isRequired,
  history: PropTypes.shape().isRequired,
  intl: PropTypes.shape().isRequired,
  pagination: PropTypes.shape(),
  shouldResetPaginationFlag: PropTypes.bool,
};

/**
 * Maps State to Props
 * @returns {object} Object with States
 */
const mapStateToProps = ({
  Notifications, ConfigurationUsers, subtitle, common,
}) => {
  const { loading, notifications, globalNotifications } = Notifications;
  const { ownUser } = ConfigurationUsers;
  const { shouldResetPagination } = common;

  const pagination = {
    pageSize: LISTING_TABLES_PAGE_SIZE,
    defaultCurrent: 1,
    onChange: null,
  };

  return {
    data: notifications,
    globalNotifications,
    loggedUser: ownUser,
    loading,
    pagination,
    subtitle,
    shouldResetPaginationFlag: shouldResetPagination,

  };
};

/**
 * Maps dispatchable actions to this.props
 * @param {Array} dispatch Dispatch
 * @returns {String} Plain object containing dispatchable actions
*/
const mapDispatchToProps = (dispatch) => ({
  notifactionsData: () => dispatch(getNotifactions()),
  globalNotificationsData: () => dispatch(getGlobalNotifications()),
  setSubtitle: (langId) => dispatch(setSubtitle(langId)),
  saveGlobalNotifications: (data) => dispatch(updateGlobalNotifications(data)),
  notifactionsOnFetchData: (page, filter) => dispatch(notifactionsOnFetchData(page, filter)),
  resetPaginationSuccess: () => dispatch(shouldResetPaginationSuccess()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(injectIntl(withRouter(Notifications)));
